import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import '../styles/new-article.css';
import api from '../api';
import Icon from './Icon';
import { Spinner } from './ui';

// Load the Google API client script once across the app lifetime
let _gapiLoadPromise = null;

function loadGapi() {
  if (window.gapi && window.google?.picker) return Promise.resolve();
  if (_gapiLoadPromise) return _gapiLoadPromise;
  _gapiLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google API script'));
    document.head.appendChild(script);
  });
  return _gapiLoadPromise;
}

function loadPicker() {
  return new Promise((resolve, reject) => {
    window.gapi.load('picker', { callback: resolve, onerror: reject });
  });
}

// UI state labels
const STATUS_LABEL = {
  idle:           null,
  checking:       'Checking…',
  authenticating: 'Connecting to Google…',
  picking:        null,  // Picker overlay handles its own UI
  importing:      'Importing from Drive…',
};

export default function DriveImportButton({ onImported, disabled }) {
  const [driveStatus, setDriveStatus] = useState(null); // null = not checked yet
  const [uiStatus, setUiStatus] = useState('idle');
  const [error, setError] = useState('');
  const popupRef = useRef(null);
  const hintId = useId();

  // Check whether Google Drive is configured on mount
  useEffect(() => {
    api.get('/gdrive/status')
      .then(r => setDriveStatus(r.data))
      .catch(() => setDriveStatus({ configured: false, authenticated: false, api_key: '', error: 'Backend unreachable' }));
  }, []);

  const handleClick = useCallback(async () => {
    setError('');

    if (!driveStatus?.configured) {
      setError('Google Drive is not configured. Add GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, and GOOGLE_API_KEY to your .env file, then restart the backend.');
      return;
    }

    try {
      setUiStatus('checking');
      // Refresh status in case token was already granted
      const freshStatus = await api.get('/gdrive/status').then(r => r.data);
      setDriveStatus(freshStatus);

      if (!freshStatus.authenticated) {
        setUiStatus('authenticating');
        // Open the OAuth popup
        const { data: { url } } = await api.get('/gdrive/auth-url');
        const popup = window.open(url, 'gdrive_auth', 'width=520,height=640,left=200,top=80');
        popupRef.current = popup;

        await new Promise((resolve, reject) => {
          const TIMEOUT_MS = 120_000;
          let settled = false;

          const settle = (fn) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            window.removeEventListener('message', msgHandler);
            clearInterval(pollClose);
            fn();
          };

          const timer = setTimeout(() => settle(() => reject(new Error('Authentication timed out'))), TIMEOUT_MS);

          const msgHandler = (e) => {
            if (e.data === 'gdrive_auth_complete') settle(resolve);
          };
          window.addEventListener('message', msgHandler);

          // Fallback: detect popup closed without sending a message
          const pollClose = setInterval(() => {
            if (popup?.closed) settle(() => reject(new Error('Authentication cancelled')));
          }, 600);
        });
      }

      // Fetch a fresh access token for the Picker
      setUiStatus('picking');
      const { data: { access_token: accessToken } } = await api.get('/gdrive/token');
      const apiKey = freshStatus.api_key || driveStatus.api_key || '';

      // Load gapi + picker library
      await loadGapi();
      await loadPicker();

      // Build and show the Picker
      await new Promise((resolve, reject) => {
        const view = new window.google.picker.DocsView()
          .setIncludeFolders(false)
          .setMimeTypes([
            'video/mp4',
            'video/quicktime',
            'video/webm',
            'video/x-matroska',
            'video/avi',
            'video/x-msvideo',
          ].join(','));

        const builder = new window.google.picker.PickerBuilder()
          .addView(view)
          .setOAuthToken(accessToken)
          .setTitle('Select a video from Google Drive')
          .setCallback(async (pickerData) => {
            const { Action, Response } = window.google.picker;
            if (pickerData[Response.ACTION] === Action.CANCEL) {
              setUiStatus('idle');
              resolve();
              return;
            }
            if (pickerData[Response.ACTION] === Action.PICKED) {
              const doc = pickerData[Response.DOCUMENTS][0];
              setUiStatus('importing');
              try {
                const res = await api.post('/gdrive/import', {
                  file_id:   doc.id,
                  file_name: doc.name,
                  mime_type: doc.mimeType,
                });
                setUiStatus('idle');
                onImported(res.data.video_path, doc.name);
                resolve();
              } catch (err) {
                setError(err.response?.data?.error || 'Import failed. Please try again.');
                setUiStatus('error');
                resolve();
              }
            }
          });

        if (apiKey) builder.setDeveloperKey(apiKey);
        builder.build().setVisible(true);
      });

    } catch (err) {
      const msg = err.message || 'Something went wrong';
      if (msg === 'Authentication cancelled') {
        setUiStatus('idle');
      } else {
        setError(msg);
        setUiStatus('error');
      }
    }
  }, [driveStatus, onImported]);

  const busy = uiStatus === 'checking' || uiStatus === 'authenticating' || uiStatus === 'importing';
  const statusLabel = STATUS_LABEL[uiStatus];

  // Styles: styles/new-article.css (na-drive). Also used by UpdateArticle.
  return (
    <div className="na-drive">
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={handleClick}
        disabled={disabled || busy || driveStatus === null}
        aria-busy={busy || undefined}
        aria-describedby={!driveStatus?.configured && driveStatus !== null ? hintId : undefined}
      >
        {busy ? (
          <>
            <Spinner size={16} />
            {statusLabel}
          </>
        ) : (
          <>
            <DriveIcon />
            Import from Google Drive
          </>
        )}
      </button>

      {error && (
        <div className="inline-msg is-error" role="alert">
          <Icon name="alert" />
          <div className="na-msg-body">{error}</div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Dismiss Google Drive error"
            onClick={() => { setError(''); setUiStatus('idle'); }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
      )}

      {!driveStatus?.configured && driveStatus !== null && (
        <div className="field-hint" id={hintId} style={{ marginTop: 0 }}>
          Add <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code>, and <code>GOOGLE_API_KEY</code> to your <code>.env</code> file to enable.
        </div>
      )}
    </div>
  );
}

function DriveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 52H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
      <path d="M43.65 25L29.9 0c-1.35.8-2.5 1.9-3.3 3.3L1.2 47.5C.4 48.9 0 50.45 0 52h27.5z" fill="#00ac47"/>
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 56.5c.8-1.4 1.2-2.95 1.2-4.5H59.8L73.55 76.8z" fill="#ea4335"/>
      <path d="M43.65 25L57.4 0H29.9L16.15 25z" fill="#00832d"/>
      <path d="M59.8 52H87.3L73.55 26H43.65z" fill="#2684fc"/>
      <path d="M43.65 25L59.8 52H27.5L43.65 25z" fill="#00ac47"/>
    </svg>
  );
}
